<template>
  <Teleport to="#portal-target" :disabled="inline">
    <transition appear name="fade-fast">
      <div
        :ref="(el) => el && (modal.current.modalRef = el)"
        role="dialog"
        data-testid="modal"
        :data-name="name"
        :class="[
          $style.dialog,
          $style[`size-${size}`],
          { [$style.white]: white, [$style.inline]: inline },
        ]"
        @keydown.esc="closeModal"
      >
        <div tabindex="0" />
        <CButton
          v-if="!disableClose"
          :aria-label="$t('common:modals.closeModal')"
          data-testid="modal-close"
          :tooltip="{
            content: $t('common:actions.close'),
            delay: { show: 1000 },
          }"
          size="sml"
          color="borderless"
          :class="$style.closeBtn"
          :style="{ '--btn-fontSize': '1rem' }"
          icon="close"
          tabindex="-1"
          @click="closeModal"
        />

        <div
          :ref="(el) => el && (modal.current.contentRef = el)"
          :class="[$style.content, props.contentClass]"
        >
          <slot :closeModal="closeModal" :context="context" />
        </div>
        <div tabindex="0" />
      </div>
    </transition>
    <transition appear name="fade-fast">
      <div
        :class="[$style.overlay, { [$style.inline]: inline }]"
        @click="closeModal"
      />
    </transition>
  </Teleport>
</template>

<script setup>
import { useModal } from '@/composables/plugins';
import { computed, watchEffect, ref } from 'vue';

import { propsDefinition } from '@/helpers/modal-manager';

const focusableElements =
  'input, select, textarea, button:not([tabindex="-1"]), [href], [tabindex]:not([tabindex="-1"])';

const emit = defineEmits(['close']);
const props = defineProps(propsDefinition);

const modal = useModal();
const lastFocus = ref(null);

const isOpen = computed(() => modal.isOpen(props.name));
const context = computed(
  () => (modal.isOpen(props.name) && modal.context) || {}
);

watchEffect(() => {
  if (!modal.current.modalRef) return;
  if (!isOpen.value) return;

  modal.current.closeModal = closeModal;

  // make sure listener has been cleaned up
  modal.current.modalRef.removeEventListener('keyup', onKey);
  modal.current.modalRef.addEventListener('keyup', onKey);
  if (!lastFocus.value) {
    // keep track of last focused element
    // to restore focus when modal is closed
    lastFocus.value = document.activeElement;
  }

  const firstFocusableEl =
    modal.current.modalRef.querySelector(focusableElements);
  firstFocusableEl?.focus();
});

function closeModal(event) {
  if (props.disableClose) return;
  modal.current?.modalRef.removeEventListener('keyup', onKey);

  // 'close' event must be emitted before calling $modal.close().
  // Listeners may rely on $modal.context which is cleared when
  // calling $modal.close().
  emit('close', event);

  modal.close(props.name);
  event?.preventDefault();
  lastFocus.value?.focus?.();
  lastFocus.value = null;
}

function onKey(event) {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeModal(event);
  }

  if (event.key === 'Tab') {
    const focusableEls =
      modal.current.modalRef.querySelectorAll(focusableElements);
    const firstFocusableElement = focusableEls[0];
    const lastFocusableElement = focusableEls[focusableEls.length - 1];

    // trap that focus!
    if (!event.shiftKey && document.activeElement === lastFocusableElement) {
      event.preventDefault();
      firstFocusableElement.focus();
    }

    if (event.shiftKey && document.activeElement === firstFocusableElement) {
      event.preventDefault();
      lastFocusableElement.focus();
    }
  }
}
</script>

<style module>
.dialog {
  --modal-max-height: 80vh;
  --modal-content-padding: 1.5rem;

  z-index: 1010;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  width: 570px;
  max-width: 90%;
  max-height: var(--modal-max-height);
  border-radius: 12px;
  background-color: var(--modalBackground);
}

.size-lg {
  width: clamp(750px, 40vw, 90%);
}

.size-xl {
  width: clamp(600px, 60vw, 90%);
}

.size-2xl {
  width: clamp(600px, 80vw, 90%);
}

.size-3xl {
  width: clamp(600px, 95vw, 95%);
}

.white {
  --modalBackground: var(--color-white);
}

.content {
  padding: var(--modal-content-padding);
  overflow-y: auto;

  & a {
    cursor: pointer;
  }
}

.overlay {
  z-index: 1000;
  position: fixed;
  inset: 0;
  background-color: rgb(0 0 0 / 39%);
  backdrop-filter: blur(2px);
}

.inline {
  position: absolute;
}

.closeBtn {
  position: absolute;
  top: var(--modal-content-padding);
  right: var(--modal-content-padding);
  z-index: 1;
}
</style>
